/**
 * TECH-2730. The completeness count behind the accordion redesign.
 *
 * The count is the whole reason the grouped list exists: an administrator
 * needs to see at a glance which messages are missing a language, without
 * opening each one. That makes the DENOMINATOR load-bearing, and it is the
 * organisation's own configured language set — not the provider portal's
 * interface locales, which is what the editor was previously limited to and
 * why a customer with Arabic- and German-speaking patients could not write to
 * them at all.
 *
 * A language is only counted when it is COMPLETE on the same terms the
 * resolver selects it by: subject and htmlContent both present. Counting a
 * half-filled translation would report a message as ready while a patient
 * preferring that language is silently downgraded to the built-in copy — the
 * exact failure the save rules in ./submission.js refuse to create.
 */

const filled = (value) => !!(value || '').trim();

const isCompleteEmail = (email) =>
  !!email && filled(email.subject) && filled(email.htmlContent);

/**
 * Every language a template actually carries, complete only.
 *
 * The base language counts via `template.template`; each entry in
 * `translations[]` counts via its own `email`. A row whose base is incomplete
 * contributes nothing at all, because the override cannot win at send time.
 *
 * @param {object} template
 * @returns {string[]} unique language codes, order not significant
 */
export const authoredLanguages = (template) => {
  if (!template) {
    return [];
  }
  const languages = new Set();
  if (template.language && isCompleteEmail(template.template)) {
    languages.add(template.language);
  }
  (template.translations || []).forEach((translation) => {
    if (
      translation &&
      translation.language &&
      isCompleteEmail(translation.email)
    ) {
      languages.add(translation.language);
    }
  });
  return [...languages];
};

/**
 * Completeness of ONE message against the organisation's configured set.
 *
 * Languages the organisation has NOT configured are ignored rather than
 * counted: a translation left over from a language since removed is still
 * stored, and counting it could report 6 of 5.
 *
 * @param {object} template
 * @param {string[]} configuredLanguages
 * @returns {{authored: number, total: number, missing: string[]}}
 */
export const messageCompleteness = (template, configuredLanguages) => {
  const configured = configuredLanguages || [];
  const authored = new Set(authoredLanguages(template));
  const missing = configured.filter((language) => !authored.has(language));
  return {
    authored: configured.length - missing.length,
    total: configured.length,
    missing,
  };
};

/**
 * Completeness of a GROUP of messages — the count on the group header.
 *
 * A purpose with no row at all still counts against the total. An organisation
 * that is missing a seeded row would otherwise show a flattering number for
 * the messages it does have, hiding the one it does not.
 *
 * @param {string[]} purposes  every purpose in the group
 * @param {object} templatesByPurpose
 * @param {string[]} configuredLanguages
 * @returns {{authored: number, total: number}}
 */
export const groupCompleteness = (
  purposes,
  templatesByPurpose,
  configuredLanguages,
) => {
  const configured = configuredLanguages || [];
  return (purposes || []).reduce(
    (acc, purpose) => {
      const { authored } = messageCompleteness(
        (templatesByPurpose || {})[purpose],
        configured,
      );
      return {
        authored: acc.authored + authored,
        total: acc.total + configured.length,
      };
    },
    { authored: 0, total: 0 },
  );
};
